<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import AssistantCard from 'dashboard/components-next/assistant/shared/AssistantCard.vue';
import CreateAgentDialog from 'dashboard/components-next/assistant/pageComponents/agent/CreateAgentDialog.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';

const store = useStore();

const uiFlags = useMapGetter('assistantAgents/getUIFlags');
const agents = useMapGetter('assistantAgents/getRecords');
const agentsMeta = useMapGetter('assistantAgents/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

// Поиск по загруженным записям (клиентская фильтрация — под масштаб SMB).
const searchQuery = ref('');
const filteredAgents = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agents.value;
  return picoSearch(agents.value, query, ['name', 'description']);
});

const dialogType = ref('');
const selectedAgent = ref(null);
const createAgentDialog = ref(null);
const deleteAgentDialog = ref(null);
// Р16 п.2: пока идёт переход «форма → подтверждение удаления», не обнуляем
// selectedAgent на закрытии формы — иначе DeleteDialog (v-if) исчезает раньше,
// чем успевает открыться, и по кнопке «Удалить» ничего не происходит.
const pendingDelete = ref(false);

const handleCreate = () => {
  dialogType.value = 'create';
  selectedAgent.value = null;
  nextTick(() => createAgentDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createAgentDialog.value.dialogRef.open());
};

const handleDelete = () => {
  nextTick(() => deleteAgentDialog.value.dialogRef.open());
};

// Клик по карточке открывает редактирование (Р11 п.3).
const handleAction = ({ action, id }) => {
  selectedAgent.value = agents.value.find(agent => id === agent.id);
  nextTick(() => {
    if (action === 'delete') handleDelete();
    else if (action === 'edit') handleEdit();
  });
};

// Удаление инициируется из формы редактирования (Р11 п.3): закрываем форму
// и открываем подтверждение удаления по выбранному агенту.
const handleDeleteFromForm = () => {
  pendingDelete.value = true;
  createAgentDialog.value?.dialogRef?.close();
  nextTick(() => {
    handleDelete();
    pendingDelete.value = false;
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  if (!pendingDelete.value) selectedAgent.value = null;
};

const fetchAgents = (page = 1) => {
  store.dispatch('assistantAgents/get', { page, kind: 'customer_agent' });
};

const onPageChange = page => fetchAgents(page);

onMounted(() => fetchAgents());
</script>

<template>
  <PageLayout
    v-model:search-query="searchQuery"
    :header-title="$t('ASSISTANT.AGENTS.HEADER')"
    :description="$t('ASSISTANT.AGENTS.PAGE_DESCRIPTION')"
    :search-placeholder="$t('ASSISTANT.AGENTS.SEARCH_PLACEHOLDER')"
    :empty-message="$t('ASSISTANT.AGENTS.EMPTY_STATE.TITLE')"
    :button-label="$t('ASSISTANT.AGENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="agentsMeta.totalCount"
    :current-page="agentsMeta.page"
    :is-fetching="isFetching"
    :is-empty="!agents.length"
    :show-pagination-footer="!isFetching && !!agents.length"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #body>
      <div
        v-if="!filteredAgents.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ $t('ASSISTANT.AGENTS.SEARCH_EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-4">
        <AssistantCard
          v-for="agent in filteredAgents"
          :id="agent.id"
          :key="agent.id"
          :name="agent.name"
          :description="agent.description"
          :updated-at="agent.updated_at"
          :inboxes="agent.inboxes || []"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateAgentDialog
      v-if="dialogType"
      ref="createAgentDialog"
      :type="dialogType"
      :selected-agent="selectedAgent"
      @close="handleCreateClose"
      @delete="handleDeleteFromForm"
    />

    <DeleteDialog
      v-if="selectedAgent"
      ref="deleteAgentDialog"
      :entity="selectedAgent"
      type="Agents"
    />
  </PageLayout>
</template>
